<template>
	<PageLoading
		:loading="loadingData || !cases.referralCase"
		class="relative mt-4"
	>
		<PageTitle>{{ t('title') }}</PageTitle>
		<PagePadding class="mt-5">
			<WrappedCard class="mt-2" topClass="p-4! flex-col! gap-3!">
				<template #top>
					<div class="flex items-center justify-between">
						<div class="text-label-primary text-sm font-medium capitalize">
							{{ t('referralLink') }}
						</div>
						<div
							class="text-label-tertiary relative inline-flex items-center gap-1 text-sm font-medium capitalize after:absolute after:-inset-2"
							@click="editCode()"
							v-hover="{ active: 'scale-95' }"
						>
							<CircleCheckIcon class="h-5 w-5" v-if="editing" />
							<div>{{ editText }}</div>
						</div>
					</div>
					<div class="flex w-full gap-2">
						<div
							class="text-label-tertiary border-separator-secondary shadow-input flex flex-1 items-center overflow-hidden rounded-lg border bg-black px-3 py-2 text-base"
							@click="editCode(true)"
							v-hover="{ active: 'scale-99', impact: 'soft' }"
							:class="{ 'animate-shake-x': failed }"
						>
							<div class="shrink-0">@ragegiftbot/</div>
							<div class="min-w-0 flex-1">
								<BaseInput
									id="code"
									class="placeholder-label-tertiary w-full uppercase"
									type="text"
									placeholder="Your_code"
									maxlength="12"
									v-model="user.referralCode"
									ref="codeInput"
									@focusout="resetToOldCode"
									v-if="editing"
								/>
								<div
									class="min-w-0 truncate uppercase"
									:class="{
										'text-label-tertiary': !user.referralCode.length,
										'text-label-primary': user.referralCode.length
									}"
									v-else
								>
									{{
										user.referralCode.length ? user.referralCode : 'YOUR_CODE'
									}}
								</div>
							</div>
							<div
								class="relative ml-3 h-5 w-5 shrink-0 after:absolute after:-inset-3"
								@click="share"
								@touchstart.stop
								v-hover="{ active: 'scale-90', impact: 'soft' }"
							>
								<Transition
									enterActiveClass="transition-all duration-500"
									leaveActiveClass="transition-all duration-500"
									leaveToClass="translate-y-full opacity-0"
									enterFromClass="-translate-y-full opacity-0"
								>
									<ShareIcon
										v-if="!loading && !success && !failed"
										class="absolute inset-0"
									/>
								</Transition>
								<Transition
									enterActiveClass="transition-all duration-500"
									leaveActiveClass="transition-all duration-500"
									leaveToClass="translate-y-full opacity-0"
									enterFromClass="-translate-y-full opacity-0"
								>
									<LoaderIcon
										v-if="loading"
										class="text-label-tertiary absolute inset-0"
									/>
								</Transition>
								<Transition
									enterActiveClass="transition-all duration-500"
									leaveActiveClass="transition-all duration-500"
									leaveToClass="translate-y-full opacity-0"
									enterFromClass="-translate-y-full opacity-0"
								>
									<CheckIcon
										v-if="success"
										class="text-green! absolute inset-0"
									/>
								</Transition>
								<Transition
									enterActiveClass="transition-all duration-500"
									leaveActiveClass="transition-all duration-500"
									leaveToClass="translate-y-full opacity-0"
									enterFromClass="-translate-y-full opacity-0"
								>
									<CloseSmIcon
										v-if="failed"
										class="text-red! absolute inset-0"
									/>
								</Transition>
							</div>
						</div>
					</div>
				</template>
				<template #bottom>
					<PartnerIcon class="text-label-secondary h-5 w-5 shrink-0" />
					<div class="text-label-secondary text-sm">
						{{ t('partnership') }}
					</div>
					<div
						class="text-label-tertiary relative ml-auto text-sm font-medium whitespace-nowrap capitalize after:absolute after:-inset-2"
						v-hover="{ active: 'scale-95' }"
						@click="openPartners"
					>
						{{ t('apply') }}
					</div>
				</template>
			</WrappedCard>
			<RouterLink
				v-if="cases.referralCase"
				custom
				v-slot="{ navigate }"
				:to="`/case/${cases.referralCase.uuid}`"
			>
				<BaseCard
					class="mt-2 p-5"
					alt
					@click="navigate"
					v-hover="{ active: 'scale-97' }"
				>
					<div class="flex items-center gap-3">
						<div class="relative h-11.75 w-11.75 shrink-0">
							<LazyImage
								:src="caseIcon(cases.referralCase.uuid, true)"
								alt="invite case"
								class="absolute -top-2.25 -left-2.25 h-15.75 w-15.75"
							/>
						</div>
						<div>
							<div class="text-label-primary text-sm font-medium capitalize">
								{{ t('freeCase') }}
							</div>
							<div class="text-label-secondary mt-1 text-sm">
								{{ inviteText }}
							</div>
						</div>
						<div
							class="text-label-tertiary ml-auto inline-flex items-center gap-0.5 py-2 pl-2"
							v-if="cases.referralCase.canOpen"
						>
							<div class="text-xs font-medium uppercase">{{ t('view') }}</div>
							<CaretRightIcon class="h-5 w-5" />
						</div>
						<EyeIcon v-else class="text-label-tertiary mr-1 ml-auto h-5 w-5" />
					</div>
					<div class="relative mt-5.5 flex justify-between">
						<div
							class="absolute top-0.75 right-0 left-0 h-2.5 rounded-full bg-[#353535]"
						></div>
						<div
							class="absolute top-0.75 left-0 h-2.5 rounded-full bg-[#F8CF1F] transition-[width] duration-1500"
							:style="{
								width: `${width}%`
							}"
						></div>
						<div
							class="relative h-4 w-4 rounded-full border-2 border-[#CEAA10] bg-[#B29106] p-0.5 shadow"
						>
							<div class="h-full w-full rounded-full bg-[#F8CF1F]"></div>
						</div>
						<div
							class="relative h-4 w-4 rounded-full border-2 p-0.5 transition-colors duration-500"
							:class="{
								'border-[#353535] bg-[#222222]': qualifiedReferrals < 1,
								'border-[#CEAA10] bg-[#B29106] shadow': qualifiedReferrals >= 1
							}"
						>
							<div
								class="h-full w-full rounded-full"
								:class="{
									'bg-[#353535]': qualifiedReferrals < 1,
									'bg-[#F8CF1F]': qualifiedReferrals >= 1
								}"
							></div>
						</div>
						<div
							class="relative h-4 w-4 rounded-full border-2 p-0.5 transition-colors duration-1000"
							:class="{
								'border-[#353535] bg-[#222222]': qualifiedReferrals < 2,
								'border-[#CEAA10] bg-[#B29106] shadow': qualifiedReferrals >= 2
							}"
						>
							<div
								class="h-full w-full rounded-full"
								:class="{
									'bg-[#353535]': qualifiedReferrals < 2,
									'bg-[#F8CF1F]': qualifiedReferrals >= 2
								}"
							></div>
						</div>
						<div
							class="relative h-4 w-4 rounded-full border-2 p-0.5 transition-colors duration-1500"
							:class="{
								'border-[#353535] bg-[#222222]': qualifiedReferrals < 3,
								'border-[#CEAA10] bg-[#B29106] shadow': qualifiedReferrals >= 3
							}"
						>
							<div
								class="h-full w-full rounded-full"
								:class="{
									'bg-[#353535]': qualifiedReferrals < 3,
									'bg-[#F8CF1F]': qualifiedReferrals >= 3
								}"
							></div>
						</div>
					</div>
				</BaseCard>
			</RouterLink>
			<BaseCard class="mt-2 flex flex-col gap-3 p-5">
				<div class="text-label-primary text-sm font-medium capitalize">
					{{ t('whoQualifies') }}
				</div>
				<div class="text-label-secondary flex items-center gap-2 text-sm">
					<PremiumIcon class="h-5 w-5" />
					<div>{{ t('premiums') }}</div>
				</div>
				<div class="text-label-secondary flex items-center gap-2 text-sm">
					<CoinIcon class="h-5 w-5" />
					<div>{{ t('depositors') }}</div>
				</div>
			</BaseCard>
			<SubtitleText class="mt-5">{{ t('yourFriend') }}</SubtitleText>
			<div class="mt-2 flex gap-1.5">
				<BenefitCard
					class="flex-col! text-center"
					:primary="t('freeCases', 3)"
					:secondary="t('onSignUp')"
				>
					<BoxIcon />
				</BenefitCard>
				<BenefitCard
					class="flex-col! text-center"
					:primary="t('bonus', [5])"
					:secondary="t('everyDeposit')"
				>
					<GiftIcon />
				</BenefitCard>
			</div>
			<template v-if="referrals.length">
				<SubtitleText class="mt-5">{{ t('recentUsers') }}</SubtitleText>
				<BaseCard
					class="mt-2 grid grid-cols-[2fr_1fr_1fr] gap-x-4 overflow-x-scroll"
				>
					<div
						class="text-label-secondary col-span-3 grid grid-cols-subgrid gap-4 px-5 py-3 text-xs font-medium uppercase"
					>
						<div class="flex-2">{{ t('user') }}</div>
						<div class="flex-1 text-center">{{ t('depositor') }}</div>
						<div class="flex-1 text-center">{{ t('premium') }}</div>
					</div>
					<div
						class="col-span-3 grid grid-cols-subgrid items-center gap-4 border-t border-black px-5 py-4"
						v-for="referral in referrals"
					>
						<div class="flex min-w-0 flex-2 items-center gap-2.5">
							<LazyImage
								:src="referral.avatar || ''"
								:alt="referral.name"
								class="h-6 w-6 shrink-0 rounded-lg"
							/>
							<div class="min-w-0 truncate">{{ referral.name }}</div>
						</div>
						<div class="flex flex-1 justify-center">
							<CircleCheckIcon
								class="h-5 w-5 text-[#F3C925]"
								v-if="referral.isDepositor"
							/>
							<CloseSmIcon class="h-5 w-5 text-[#777777]" v-else />
						</div>
						<div class="flex flex-1 justify-center">
							<CircleCheckIcon
								class="h-5 w-5 text-[#F3C925]"
								v-if="referral.isPremium"
							/>
							<CloseSmIcon class="h-5 w-5 text-[#777777]" v-else />
						</div>
					</div>
				</BaseCard>
			</template>
		</PagePadding>
	</PageLoading>
</template>
<script setup lang="ts">
import PageTitle from '@/components/PageTitle.vue'
import PagePadding from '@/components/PagePadding.vue'
import SubtitleText from '@/components/SubtitleText.vue'
import BaseCard from '@/components/BaseCard.vue'
import CoinIcon from '@/assets/icons/coin.svg'
import BaseInput from '@/components/BaseInput.vue'
import { ref, nextTick, computed, watch } from 'vue'
import LazyImage from '@/components/LazyImage.vue'
import PageLoading from '@/components/PageLoading.vue'
import { useUser } from '@/stores/user'
import { client } from '@/api/orpc'
import { sleep } from '@/utils/sleep'
import LoaderIcon from '@/assets/icons/loader.svg'
import CheckIcon from '@/assets/icons/check.svg'
import CloseSmIcon from '@/assets/icons/close-sm.svg'
import { WebApp } from '@grammyjs/web-app'
import { useLayout } from '@/stores/layout'
import { useToast } from '@/stores/toast'
import { useI18n } from 'vue-i18n'
import BoxIcon from '@/assets/icons/box.svg'
import GiftIcon from '@/assets/icons/gift.svg'
import BenefitCard from '@/components/BenefitCard.vue'
import CircleCheckIcon from '@/assets/icons/circle-check.svg'
import WrappedCard from '@/components/WrappedCard.vue'
import PartnerIcon from '@/assets/icons/partner.svg'
import ShareIcon from '@/assets/icons/share.svg'
import { useCases } from '@/stores/cases'
import { caseIcon } from '@/utils/urls'
import CaretRightIcon from '@/assets/icons/caret-right.svg'
import PremiumIcon from '@/assets/icons/premium.svg'
import { debounce } from '@/utils/debounce'
import EyeIcon from '@/assets/icons/eye.svg'

const { t } = useI18n({
	messages: {
		en: {
			title: 'invites',
			affiliateEarnings: 'Affiliate Earnings',
			availableEarnings: 'available earnings',
			yourFriendGets: 'Your friend gets',
			referralLink: 'affiliate link',
			claim: 'claim',
			referral: 'Referral',
			depositBonus: '+{0}% deposit bonus',
			referralLinkDesk:
				'Users will be prompted to claim your code when they click this link.',
			youEarn: 'You earn',
			yourFriend: 'Your friend gets',
			ofDeposits: '{0}% of deposits',
			cutOfDeposits: 'cut of every referral deposit',
			ofHouseEdge: '{0}% of house edge',
			cutOfHouseEdge: 'cut of every unboxing and battle',
			freeCases: '0 free case | 1 free case | {n} free cases',
			onSignUp: 'on sign up',
			bonus: '+{0}% bonus',
			everyDeposit: 'on every deposit',
			nextDeposit: 'on his next deposit',
			share: 'Share',
			dashboard: 'Dashboard',
			earnings: 'earnings',
			totalOpened: 'Total opened',
			activeReferrals: 'Active Referrals',
			totalCommission: 'Total commission',
			topUsers: 'Top-10 Users',
			recentUsers: 'Recent Activations',
			users: 'users',
			depositor: 'depositor',
			premium: 'premium',
			edit: 'Edit',
			save: 'Save',
			copied: 'Copied to clipboard',
			cta: `\n💢 Join me on {'@'}ragegift ‼️`,
			earning: {
				top: 'Top earning',
				worst: 'Worst earning'
			},
			notEnough: 'Not enough referral coins to claim',
			period: {
				day: '1 day',
				week: '1 week',
				month: '1 month',
				all: 'All-time'
			},
			partnership: 'Interested in partnership?',
			apply: 'Apply now',
			freeCase: 'Free Gold Mine Case',
			inviteToClaim: 'Invite {n} friends to claim',
			inviteMore:
				'Invite {n} more friend to claim | Invite {n} more friends to claim',
			readyToClaim: 'Ready to claim!',
			view: 'View',
			whoQualifies: 'Who qualifies:',
			premiums: 'Premium Subscribers',
			depositors: 'Depositors under your code',
			alreadyClaimed: 'Already claimed',
			codeInUse: 'Code {0} is already in use',
			maintenance: 'Service under maintenance'
		},
		ru: {
			title: 'рефералы',
			affiliateEarnings: 'заработок',
			availableEarnings: 'доступно к выводу',
			yourFriendGets: 'Твой друг получит',
			referralLink: 'реферальная ссылка',
			claim: 'забрать',
			referral: 'Рефералы',
			depositBonus: '+{0}% к депозиту',
			referralLinkDesk:
				'При переходе другу будет предложено активировать ваш код.',
			youEarn: 'Ты получаешь',
			yourFriend: 'Твой друг получает',
			ofDeposits: '{0}% от депозитов',
			cutOfDeposits: 'с каждого депозита реферала',
			ofHouseEdge: '{0}% от house edge',
			cutOfHouseEdge: 'с каждого открытия и батла',
			freeCases: '0 кейсов | 1 кейс | {n} кейса | {n} кейсов',
			onSignUp: 'бесплатно',
			bonus: '+{0}% бонус',
			nextDeposit: 'на его следующий депозит',
			everyDeposit: 'на пополнения',
			share: 'Поделиться',
			dashboard: 'Статистика',
			earnings: 'заработано',
			totalOpened: 'Всего открыто',
			activeReferrals: 'Активных рефералов',
			totalCommission: 'Всего комиссионных',
			topUsers: 'Топ-10 игроков',
			recentUsers: 'Последние активации',
			users: 'игроков',
			depositor: 'пополнил',
			premium: 'премиум',
			edit: 'изменить',
			save: 'Сохранить',
			copied: 'Скопировано',
			cta: `\n💢 Вступай в батл на {'@'}ragegift ‼️`,
			earning: {
				top: 'Сначала лучшие',
				worst: 'Сначала худшие'
			},
			notEnough: 'Недостаточно реферального баланса для вывода',
			period: {
				day: '1 день',
				week: '1 неделя',
				month: '1 месяц',
				all: 'Всё время'
			},
			partnership: 'Интересует партнерство?',
			apply: 'связаться',
			freeCase: 'Бесплатный Золотой кейс',
			inviteToClaim: 'Пригласи {n}х друзей, чтобы забрать',
			inviteMore:
				'Пригласи {n}-го друга, чтобы забрать | Пригласи {n}-х друзей, чтобы забрать',
			readyToClaim: 'Готов к получению!',
			view: '',
			whoQualifies: 'Кто подходит:',
			premiums: 'Премиум Подписчики',
			depositors: 'Пополнившие по твоему коду',
			alreadyClaimed: 'Уже открыт',
			codeInUse: 'Код {0} уже используется',
			maintenance: 'Сервис на техническом обслуживании'
		}
	}
})

const layout = useLayout()

layout.showBottomNavbar()

const user = useUser()
const cases = useCases()

const codeInput = ref<any>()
const editing = ref(false)

const toast = useToast()

let oldCode = ''

const editCode = async (forceEdit?: boolean) => {
	if (editing.value && !forceEdit) {
		saveCode()
		return
	}

	if (editing.value && forceEdit) {
		return
	}

	oldCode = user.referralCode

	editing.value = true

	await nextTick()

	if (!codeInput.value) {
		return
	}

	codeInput.value.focus()
	codeInput.value.select()
}

const resetToOldCode = async () => {
	await nextTick()
	await sleep(100)

	if (!editing.value) {
		return
	}

	user.referralCode = oldCode
	editing.value = false
	oldCode = ''
}

const editText = computed(() => (editing.value ? t('save') : t('edit')))

const loading = ref(false)
const success = ref(false)
const failed = ref(false)

const onSaveError = async () => {
	user.referralCode = oldCode
	oldCode = ''
	failed.value = true
	WebApp.HapticFeedback.notificationOccurred('error')
	await sleep(1500)
	failed.value = false
}

const saveCode = async () => {
	console.info('saveCode')
	editing.value = false

	if (oldCode === user.referralCode) {
		return
	}

	loading.value = true
	success.value = false
	failed.value = false

	if (!user.referralCode.length) {
		user.referralCode = user.telegramId
	}

	const { error, data, isDefined } = await client.user.updateReferralCode({
		referralCode: user.referralCode
	})

	loading.value = false

	if (isDefined) {
		if (error.code === 'REFERRAL_CODE_ALREADY_IN_USE') {
			toast.add({
				type: 'error',
				text: t('codeInUse', [user.referralCode])
			})
		}
		if (error.code === 'MAINTENANCE') {
			toast.add({
				type: 'info',
				text: t('maintenance')
			})
		}
	}

	if (error) {
		onSaveError()
		return console.error(error)
	}

	if (!data) {
		onSaveError()
		return
	}

	WebApp.HapticFeedback.notificationOccurred('success')

	success.value = true
	await sleep(1500)
	success.value = false
}

const share = () => {
	const cta = t('cta')
	const link = `https://t.me/share/url?url=${encodeURIComponent(`https://t.me/ragegiftbot/app?startapp=r-${user.referralCode.toUpperCase()}`)}&text=${encodeURIComponent(cta)}`
	WebApp.openTelegramLink(link)
}

const loadingData = ref(true)
const referrals = ref<
	{
		name: string
		avatar: string | null
		isDepositor: boolean
		isPremium: boolean
	}[]
>([])
const periods = ref<
	Record<
		'1day' | '1week' | '1month' | 'all',
		{
			totalEarnings: number
			totalSpent: number
			totalReferrals: number
			chartData: {
				date: string
				earnings: number
				spent: number
			}[]
		}
	>
>()

const getReferralData = async () => {
	loadingData.value = true

	const { error, data } = await client.user.getReferralAnalytics()

	loadingData.value = false

	if (error) {
		return console.error(error)
	}

	if (!data) {
		return
	}

	referrals.value = data.referrals
}

watch(
	() => user.uuid,
	() => {
		if (!user.uuid) {
			return
		}
		if (periods.value) {
			return
		}

		getReferralData()
	},
	{ immediate: true }
)

const inviteText = computed(() => {
	if (!cases.referralCase?.qualifiedReferrals) {
		return t('inviteToClaim', 3)
	}
	if (
		cases.referralCase.qualifiedReferrals >=
			cases.referralCase.requiredReferrals &&
		!cases.referralCase.canOpen
	) {
		return t('alreadyClaimed')
	}
	if (
		cases.referralCase.qualifiedReferrals >=
			cases.referralCase.requiredReferrals &&
		cases.referralCase.canOpen
	) {
		return t('readyToClaim')
	}

	return t(
		'inviteMore',
		cases.referralCase
			? cases.referralCase.requiredReferrals -
					cases.referralCase.qualifiedReferrals
			: 3
	)
})

const width = computed(() => {
	if (!cases.referralCase || loadingData.value) {
		return 0
	}

	return Math.min(
		(cases.referralCase.qualifiedReferrals /
			cases.referralCase.requiredReferrals) *
			100,
		100
	)
})

const qualifiedReferrals = computed(() => {
	return cases.referralCase && !loadingData.value
		? cases.referralCase.qualifiedReferrals
		: 0
})

const openPartners = () => {
	WebApp.openTelegramLink('https://t.me/RageGiftPartners/4')
}
</script>
